/**
 * useTaskAttachments - Hook para gerenciar anexos de tarefas
 * Reutiliza mesma lógica e bucket do CRM (useDealAttachments)
 * 
 * Versão: 1.1.0
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-06 v1.1.0 - Adicionada função standalone uploadTaskAttachmentDirect
 * RELEASE: 2026-10-04 v2.0.0 - Regra passa de allowlist para blocklist: aceita qualquer arquivo
 *          (vídeos, áudios, ZIP/RAR etc.) exceto executáveis/scripts perigosos; limite 50 MB;
 *          bloqueia extensões duplas (ex.: nota.pdf.exe) e arquivos sem extensão.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';
import { sanitizeStorageFileName } from '@/lib/storageFileName';
import { ATTACHMENT_MAX_BYTES, ATTACHMENT_MAX_BATCH, validateAttachmentFile } from '../../supabase/functions/_shared/attachmentFileRules';

// Tipos de arquivo suportados (mesmos do CRM)
export type AttachmentFileType = 'image' | 'pdf' | 'document' | 'spreadsheet' | 'video' | 'audio' | 'archive' | 'other';

// Interface do anexo de tarefa
export interface TaskAttachment {
  id: string;
  tenant_id: string;
  task_id: string;
  file_name: string;
  file_type: string;
  mime_type: string | null;
  file_size: number | null;
  storage_path: string;
  public_url: string;
  thumbnail_url: string | null;
  metadata: Record<string, unknown>;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

// Limites de upload
const MAX_FILE_SIZE = ATTACHMENT_MAX_BYTES;
const MAX_ATTACHMENTS = ATTACHMENT_MAX_BATCH;

/** Rótulo exibido nas áreas de envio do módulo Tarefas */
export const TASK_ATTACHMENT_SIZE_LABEL = 'Máx: 50MB por arquivo';

const ARCHIVE_EXT = new Set(['zip', 'rar', '7z', 'tar', 'gz', 'tgz', 'bz2', 'xz']);
const SPREADSHEET_EXT = new Set(['xls', 'xlsx', 'csv', 'ods']);
const AUDIO_EXT = new Set(['opus', 'ogg', 'm4a', 'mp3', 'wav', 'aac', 'amr']);
const VIDEO_EXT = new Set(['mp4', 'mov', 'webm', 'avi', 'mkv', '3gp', 'm4v']);

/** Extrai a extensão final em minúsculas */
function getExtension(name: string): string {
  const parts = name.toLowerCase().split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

/** Determina o tipo do arquivo pelo MIME type e, em fallback, pela extensão */
function getFileType(mimeType: string, fileName = ''): AttachmentFileType {
  const ext = getExtension(fileName);
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/') || VIDEO_EXT.has(ext)) return 'video';
  if (mimeType.startsWith('audio/') || AUDIO_EXT.has(ext)) return 'audio';
  if (mimeType === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (SPREADSHEET_EXT.has(ext) || mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'spreadsheet';
  if (ARCHIVE_EXT.has(ext) || mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('compressed')) return 'archive';
  if (['doc', 'docx', 'txt', 'rtf', 'odt', 'ppt', 'pptx'].includes(ext) || mimeType.startsWith('text/')) return 'document';
  return 'other';
}

/** Valida se o arquivo é permitido (blocklist + limite de tamanho) */
export function validateFile(file: File): { valid: boolean; error?: string } {
  // A mesma regra é consumida pelo portal externo e pelo servidor de anexos.
  return validateAttachmentFile(file);
}

/** Sanitiza o nome do arquivo para segurança */
// v1.x: delegado ao utilitário central (remove emojis/acentos e evita
// o erro "Invalid key" do Supabase Storage). O nome original permanece no banco.
function sanitizeFileName(fileName: string): string {
  return sanitizeStorageFileName(fileName);
}

/**
 * Função standalone para upload de anexo de tarefa.
 * Não depende do hook — pode ser chamada imperativamente após criação da tarefa.
 * 
 * @param taskId - ID da tarefa recém-criada
 * @param tenantId - ID do tenant
 * @param file - Arquivo a ser enviado
 * @returns Dados do anexo criado
 */
export async function uploadTaskAttachmentDirect(
  taskId: string,
  tenantId: string,
  file: File,
  taskCommentId?: string, // ID do andamento para vincular anexo diretamente
  taskCommentReplyId?: string, // ID da resposta de comentário para vincular anexo
  checklistItemCommentId?: string // ID do comentário do item de checklist para vincular anexo
): Promise<TaskAttachment> {
  // Validação do arquivo
  const validation = validateFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  const fileType = getFileType(file.type, file.name);
  const sanitizedName = sanitizeFileName(file.name);
  const timestamp = Date.now();
  const storagePath = `${tenantId}/tasks/${taskId}/${timestamp}_${sanitizedName}`;

  // Upload para o bucket "tarefas" (dedicado ao módulo de tarefas)
  const { error: uploadError } = await supabase.storage
    .from('tarefas')
    .upload(storagePath, file, {
      cacheControl: '3600',
      upsert: false,
      // MIME vazio → octet-stream (força download, nunca execução)
      contentType: file.type || 'application/octet-stream',
    });

  if (uploadError) {
    logger.error('Erro no upload de anexo de tarefa:', uploadError.message);
    throw new Error('Falha no upload do arquivo');
  }

  // Obter URL pública
  const { data: urlData } = supabase.storage
    .from('tarefas')
    .getPublicUrl(storagePath);

  // Criar registro no banco
  const { data: attachment, error: insertError } = await supabase
    .from('task_attachments')
    .insert({
      tenant_id: tenantId,
      task_id: taskId,
      file_name: sanitizedName,
      file_type: fileType,
      mime_type: file.type || 'application/octet-stream',
      file_size: file.size,
      file_path: storagePath,
      storage_path: storagePath,
      public_url: urlData.publicUrl,
      thumbnail_url: fileType === 'image' ? urlData.publicUrl : null,
      uploaded_by: (await supabase.auth.getUser()).data.user?.id,
      task_comment_id: taskCommentId || null, // Vincula ao andamento se fornecido
      task_comment_reply_id: taskCommentReplyId || null, // Vincula à resposta se fornecido
      checklist_item_comment_id: checklistItemCommentId || null, // Vincula ao comentário do item de checklist
    })
    .select()
    .single();

  if (insertError) {
    // Rollback: remove arquivo do storage se insert falhar
    await supabase.storage.from('tarefas').remove([storagePath]);
    logger.error('Erro ao registrar anexo de tarefa:', insertError.message);
    throw new Error('Falha ao registrar anexo');
  }

  return attachment as TaskAttachment;
}

/**
 * Hook principal para gerenciar anexos de tarefas
 */
export function useTaskAttachments(taskId: string | undefined) {
  const { toast } = useToast();
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Query para buscar anexos da tarefa
  const attachmentsQuery = useQuery({
    queryKey: ['task-attachments', taskId],
    queryFn: async () => {
      if (!taskId) return [];

      // Busca via RPC server-side (scope 'task' = anexos diretos, sem andamento)
      const { data, error } = await supabase.rpc('get_task_attachments', {
        p_task_id: taskId,
        p_scope: 'task',
      });

      if (error) {
        logger.error('Erro ao buscar anexos de tarefa via RPC:', error.message);
        throw error;
      }

      // RPC retorna JSONB — parseia se necessário
      const attachments = (typeof data === 'string' ? JSON.parse(data) : data) as TaskAttachment[];
      return attachments || [];
    },
    enabled: !!taskId,
  });

  // Mutation para upload de arquivo (usa função standalone internamente)
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!taskId || !currentTenant?.id) {
        throw new Error('Tarefa ou tenant não definido');
      }

      const currentCount = attachmentsQuery.data?.length || 0;
      if (currentCount >= MAX_ATTACHMENTS) {
        throw new Error(`Limite de ${MAX_ATTACHMENTS} anexos atingido`);
      }

      return uploadTaskAttachmentDirect(taskId, currentTenant.id, file);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-attachments', taskId] });
      // Mantém aba "Anexos" agregada em sincronia (RPC get_task_activity_attachments)
      queryClient.invalidateQueries({ queryKey: ['task-activity-attachments', taskId] });
      // v1.2.0 — invalida indicador de uso (storage) imediatamente após upload
      queryClient.invalidateQueries({ queryKey: ['tasks-usage'] });
      toast({ title: 'Arquivo enviado', description: 'Anexo adicionado com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro no upload', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation para deletar anexo
  const deleteMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      if (!taskId) throw new Error('Tarefa não definida');

      const { data: attachment, error: fetchError } = await supabase
        .from('task_attachments')
        .select('storage_path')
        .eq('id', attachmentId)
        .single();

      if (fetchError || !attachment) {
        throw new Error('Anexo não encontrado');
      }

      // Deletar do storage
      if (attachment.storage_path) {
        const { error: storageError } = await supabase.storage
          .from('tarefas')
          .remove([attachment.storage_path]);
        if (storageError) {
          logger.warn('Erro ao deletar do storage:', storageError.message);
        }
      }

      // Deletar registro
      const { error: deleteError } = await supabase
        .from('task_attachments')
        .delete()
        .eq('id', attachmentId);

      if (deleteError) {
        throw new Error('Falha ao deletar anexo');
      }

      return attachmentId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-attachments', taskId] });
      // Mantém aba "Anexos" agregada em sincronia (RPC get_task_activity_attachments)
      queryClient.invalidateQueries({ queryKey: ['task-activity-attachments', taskId] });
      // v1.2.0 — invalida indicador de uso (storage) imediatamente após exclusão
      queryClient.invalidateQueries({ queryKey: ['tasks-usage'] });
      toast({ title: 'Anexo removido', description: 'Arquivo excluído com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao excluir', description: error.message, variant: 'destructive' });
    },
  });

  return {
    attachments: attachmentsQuery.data || [],
    isLoading: attachmentsQuery.isLoading,
    error: attachmentsQuery.error,
    uploadFile: uploadMutation.mutateAsync,
    deleteAttachment: deleteMutation.mutate,
    isUploading: uploadMutation.isPending,
    isDeleting: deleteMutation.isPending,
    canAddMore: (attachmentsQuery.data?.length || 0) < MAX_ATTACHMENTS,
    maxAttachments: MAX_ATTACHMENTS,
    maxFileSize: MAX_FILE_SIZE,
  };
}
